/** Session flag survives `_chunk_reload` URL cleanup before async lazy chunks finish loading. */
const CHUNK_RECOVERY_STORAGE_KEY = 'vite-chunk-recovery-attempted';

/** After a successful recovery window, allow one automatic retry on the next deploy. */
const CHUNK_RECOVERY_SUCCESS_CLEAR_MS = 90_000;

let successClearTimer: number | undefined;

export function chunkRecoveryWasAttempted(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (sessionStorage.getItem(CHUNK_RECOVERY_STORAGE_KEY) === '1') return true;
  } catch {
    /* private mode / blocked storage */
  }
  return new URL(window.location.href).searchParams.has('_chunk_reload');
}

export function markChunkRecoveryAttempted(): void {
  try {
    sessionStorage.setItem(CHUNK_RECOVERY_STORAGE_KEY, '1');
  } catch {
    /* private mode */
  }
}

export function clearChunkRecoveryAttempted(): void {
  try {
    sessionStorage.removeItem(CHUNK_RECOVERY_STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

export function stripChunkReloadFromUrl(): void {
  const cleanUrl = new URL(window.location.href);
  if (!cleanUrl.searchParams.has('_chunk_reload')) return;
  cleanUrl.searchParams.delete('_chunk_reload');
  window.history.replaceState(window.history.state, '', cleanUrl.toString());
}

function cancelChunkRecoverySuccessClear(): void {
  if (successClearTimer === undefined) return;
  window.clearTimeout(successClearTimer);
  successClearTimer = undefined;
}

/** Drop the attempt flag after the shell and lazy routes had time to load without another preload error. */
export function scheduleChunkRecoverySuccessClear(): void {
  if (typeof window === 'undefined') return;
  cancelChunkRecoverySuccessClear();
  const run = () => {
    successClearTimer = window.setTimeout(() => {
      successClearTimer = undefined;
      if (!document.querySelector('[data-chunk-recovery-banner]')) {
        clearChunkRecoveryAttempted();
      }
    }, CHUNK_RECOVERY_SUCCESS_CLEAR_MS);
  };
  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run, { once: true });
  }
}

function showChunkRecoveryBanner(): void {
  if (document.querySelector('[data-chunk-recovery-banner]')) return;
  cancelChunkRecoverySuccessClear();
  const url = new URL(window.location.href);
  url.searchParams.delete('_chunk_reload');
  const banner = document.createElement('div');
  banner.setAttribute('data-chunk-recovery-banner', '');
  banner.setAttribute('role', 'alert');
  banner.style.cssText =
    'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;padding:1.5rem;background:rgba(0,0,0,.45);font:14px/1.5 system-ui,sans-serif';
  banner.innerHTML =
    '<div style="max-width:28rem;border-radius:8px;background:#fff;color:#111;padding:1.25rem;box-shadow:0 8px 30px rgba(0,0,0,.2)">' +
    '<p style="margin:0 0 .75rem"><strong>Updated app files could not load.</strong></p>' +
    '<p style="margin:0 0 1rem;color:#444">Clear this site’s cache (or use a private window), then reload.</p>' +
    '<button type="button" style="padding:.5rem 1rem;border:0;border-radius:6px;background:#111;color:#fff;cursor:pointer">Reload</button></div>';
  banner.querySelector('button')?.addEventListener('click', () => {
    clearChunkRecoveryAttempted();
    window.location.replace(url.toString());
  });
  document.body.appendChild(banner);
}

export function handleVitePreloadError(event: Event): void {
  event.preventDefault();
  cancelChunkRecoverySuccessClear();

  if (chunkRecoveryWasAttempted()) {
    stripChunkReloadFromUrl();
    showChunkRecoveryBanner();
    return;
  }

  markChunkRecoveryAttempted();
  const url = new URL(window.location.href);
  url.searchParams.set('_chunk_reload', String(Date.now()));
  window.location.replace(url.toString());
}

/** Persist recovery state, clean the address bar, and allow lazy chunks to finish before clearing the flag. */
export function initChunkRecoveryFromUrl(): void {
  if (typeof window === 'undefined') return;
  if (!new URL(window.location.href).searchParams.has('_chunk_reload')) return;
  markChunkRecoveryAttempted();
  stripChunkReloadFromUrl();
  scheduleChunkRecoverySuccessClear();
}
